/**
 * Several roles proposed together with "Propose in bulk", as the one request they were filed as.
 *
 * Before this card each role was its own `ProposalCard`, with the same title, rationale, manager and
 * start date — the only difference being "Track & level" in the middle of an eight-field grid. The
 * first real bulk request (two Customer Care backfills, Oct 2 2026) was read as a duplicate and one
 * role was declined for it, while the Slack announcement had arrived as ONE message (Anna Kaiser,
 * Luisa Heinzinger). So the shared facts are said once here, the roles are listed beneath them by
 * level, and each approver can decide them all at once or one by one (David, Oct 6 2026).
 *
 * "Approve all" decides only the roles still WAITING on that approver — never one they have already
 * decided. "Approve the IC3, decline the IC1" must survive a later bulk click, so a changed mind is
 * recorded on the role itself. The proxy applies the same rule (`submissionDecisionTargets`); the
 * count on the button is only a preview of it.
 */
import { useState } from 'react';
import type {
  ApprovalDecision,
  ApproverKey,
  ApprovalViewer,
  PriorityRating,
  ProposalStatus,
  RoleProposal,
} from '../../types/roleApprovals';
import { formatEuro, type RoleCompensation } from '../../types/compensation';
import { ApprovalControls } from './ApprovalControls';
import { ProposalCard } from './ProposalCard';
import { Fact, RADIUS, StatusPill, shortDate } from './approvalUi';

interface Props {
  submissionId: string;
  /** In the order the server returned them; at least two. */
  proposals: RoleProposal[];
  viewer: ApprovalViewer;
  busy: boolean;
  onDecideAll: (
    submissionId: string,
    approverKey: ApproverKey,
    decision: Exclude<ApprovalDecision, null>,
    reason?: string,
    /** The CEO's unsaved per-role rating picks, sent with HIS "Approve all" only. */
    priorityRatings?: Record<string, PriorityRating | null>
  ) => void;
  onDecide: (
    proposalId: string,
    approverKey: ApproverKey,
    decision: ApprovalDecision,
    reason?: string,
    priorityRating?: PriorityRating | null
  ) => void;
  onRetryTransfer: (proposalId: string) => void;
  onSetPriority: (proposalId: string, rating: PriorityRating | null) => void;
  priorityOwner: string;
}

/** A role no decision can be recorded on any more — same locks as `ProposalCard`. */
const isLocked = (p: RoleProposal) => Boolean(p.hiringPlanRecordId) || p.status === 'Cancelled';

/** "1 approved · 1 declined", or the single status when every role shares it. */
function statusSummary(proposals: RoleProposal[]): ProposalStatus | string {
  const counts = new Map<ProposalStatus, number>();
  proposals.forEach((p) => {
    const status = p.status ?? 'Proposed';
    counts.set(status, (counts.get(status) ?? 0) + 1);
  });
  if (counts.size === 1) return [...counts.keys()][0];
  const words: Record<ProposalStatus, string> = {
    Proposed: 'awaiting approval',
    Approved: 'approved',
    Declined: 'declined',
    Cancelled: 'cancelled',
  };
  return (['Proposed', 'Approved', 'Declined', 'Cancelled'] as ProposalStatus[])
    .filter((s) => counts.has(s))
    .map((s) => `${counts.get(s)} ${words[s]}`)
    .join(' · ');
}

/** "Approved 1 of 2 · declined 1", for one approver across the roles. */
function approverSummary(proposals: RoleProposal[], key: ApproverKey): string {
  const states = proposals.map((p) => p.approvals.find((a) => a.key === key));
  // Per verdict, how many a former holder made — their name, not the one beside this row.
  const count = (verdict: 'Approved' | 'Declined') => {
    const these = states.filter((a) => a?.decision === verdict);
    const former = these.filter((a) => a?.decidedByName).map((a) => a?.decidedByName as string);
    if (!these.length) return null;
    const label = `${verdict.toLowerCase()} ${these.length}`;
    return former.length ? `${label} (${former.length} by ${[...new Set(former)].join(', ')})` : label;
  };
  const waiting = states.filter((a) => !a?.decision).length;
  const parts = [count('Approved'), count('Declined')].filter(Boolean);
  if (waiting) parts.push(`${waiting} waiting`);
  return parts.join(' · ');
}

/**
 * A bulk submission's roles as one card, with "all" controls on every office the viewer signs for
 * (`viewer.approverKeys`) and each role still decidable on its own.
 */
export function ProposalGroupCard({
  submissionId,
  proposals,
  viewer,
  busy,
  onDecideAll,
  onDecide,
  onRetryTransfer,
  onSetPriority,
  priorityOwner,
}: Props) {
  // The CEO's unsaved rating pick per role. Held HERE, not in each role card, because "Approve all"
  // has to send them: approving a role approves the rating on screen (see `PriorityPanel`), and a
  // bulk click that approved the stored rating instead would silently drop his choice.
  const [picks, setPicks] = useState<Record<string, PriorityRating | null | undefined>>({});
  const first = proposals[0];
  const total = proposals.length;
  const summary = statusSummary(proposals);
  const costs = proposals
    .map((p) => p.compensation)
    .filter((c): c is RoleCompensation => Boolean(c) && !c?.unavailable && Number.isFinite(c?.costUntilYearEnd));
  const year = costs[0]?.year;

  return (
    <article className={`${RADIUS} border border-zinc-200 bg-white p-4`} aria-label={`${first.title}, ${total} roles`}>
      <header className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-zinc-900">
            {first.title}{' '}
            <span className="font-normal text-zinc-500">· {total} roles, filed together</span>
          </h2>
          <p className="text-xs text-zinc-500">
            {[first.peopleTeam, first.role].filter(Boolean).join(' · ') || 'No department set'}
          </p>
        </div>
        {summary === 'Proposed' || summary === 'Approved' || summary === 'Declined' || summary === 'Cancelled' ? (
          <StatusPill status={summary} />
        ) : (
          <span className="text-xs font-medium text-zinc-600">{summary}</span>
        )}
      </header>

      <div className="mb-3 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
        <Fact label="Hiring manager" value={first.hiringManager} />
        <Fact label="Levels" value={proposals.map((p) => p.trackAndLevel || '—').join(', ')} />
        <Fact label="Unit" value={first.unit} />
        <Fact label="Reason" value={first.reasonForHiring} />
        <Fact label="Cost category" value={first.costCategory} />
        <Fact label="Proposed by" value={first.proposedBy} />
        <Fact label="Proposed on" value={shortDate(first.createdOn)} />
        {/* The sum, beside the per-role figures below — the question being decided is what the
            request costs, and adding up two cards in your head is how a figure gets misread. */}
        {costs.length > 0 && (
          <Fact
            label={`Cost until end of ${year}`}
            value={`${formatEuro(costs.reduce((acc, c) => acc + (c.costUntilYearEnd ?? 0), 0))}${
              costs.length < total ? ` (${costs.length} of ${total} roles costed)` : ` (all ${total} roles)`
            }`}
          />
        )}
      </div>

      {first.rationale && (
        <p className={`${RADIUS} mb-3 border-l-2 border-zinc-200 bg-zinc-50 px-3 py-2 text-xs text-zinc-700`}>
          {first.rationale}
        </p>
      )}

      <section aria-label="Decide all roles" className="mb-3 flex flex-col gap-3 border-t border-zinc-100 pt-3">
        <p className="text-xs text-zinc-500">
          Decide all {total} roles at once here, or each role on its own below. &ldquo;All&rdquo; only
          covers the roles you have not decided yet.
        </p>
        {first.approvals.map((approver) => {
          const isSelf = viewer.approverKeys.includes(approver.key);
          const waiting = proposals.filter(
            (p) => !isLocked(p) && p.approvals.find((a) => a.key === approver.key)?.decision == null
          ).length;
          const canDecide = waiting > 0 && (isSelf || viewer.isAdmin);
          return (
            <div key={approver.key} className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex min-w-0 flex-wrap items-baseline gap-2">
                <span className="text-xs font-medium text-zinc-700">{approver.title}</span>
                <span className="text-xs text-zinc-500">{approver.name}</span>
                <span className="text-xs text-zinc-600">{approverSummary(proposals, approver.key)}</span>
              </div>
              {canDecide && (
                <ApprovalControls
                  // Decision null: the bulk control never offers "Withdraw", which is per role.
                  approver={{ ...approver, decision: null, decidedAt: null }}
                  isSelf={isSelf}
                  isDelegate={viewer.delegateKeys.includes(approver.key)}
                  disabled={busy}
                  approveLabel={waiting === total ? `Approve all ${total}` : `Approve the ${waiting} waiting`}
                  declineLabel={waiting === total ? `Decline all ${total}` : `Decline the ${waiting} waiting`}
                  onDecide={(key, decision, reason) => {
                    if (!decision) return;
                    const carriesRatings = key === 'ceo' && decision === 'Approved' && viewer.canSetPriority;
                    if (!carriesRatings) return onDecideAll(submissionId, key, decision, reason);
                    const ratings = Object.fromEntries(
                      proposals.flatMap((p) => {
                        const pick = picks[p.id];
                        const shown = pick !== undefined ? pick : (p.priority?.approved ?? p.priority?.suggested ?? null);
                        return [[p.id, shown]];
                      })
                    );
                    onDecideAll(submissionId, key, decision, reason, ratings);
                    setPicks({});
                  }}
                />
              )}
            </div>
          );
        })}
      </section>

      <div className="flex flex-col gap-2">
        {proposals.map((proposal, index) => (
          <ProposalCard
            key={proposal.id}
            proposal={proposal}
            member={{ index, total }}
            pickedRating={picks[proposal.id]}
            onPickedRatingChange={(rating) => setPicks((all) => ({ ...all, [proposal.id]: rating }))}
            viewer={viewer}
            busy={busy}
            onDecide={onDecide}
            onRetryTransfer={onRetryTransfer}
            onSetPriority={onSetPriority}
            priorityOwner={priorityOwner}
          />
        ))}
      </div>
    </article>
  );
}
